import { detectCarrier, trackingUrl } from "../../components/site/TrackPackage";
import React, { useEffect, useState } from "react";
import { Trash2, Loader2, Inbox, Mail, Phone, Star, Send, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export const STATUSES = [
  ["new", "Request received", "bg-[#fff3dc] text-[#a0620a]"],
  ["offer_sent", "Offer sent", "bg-[#e0efff] text-[#1d5fb3]"],
  ["shipped", "Package on the way", "bg-[#efe7ff] text-[#5b3db3]"],
  ["received", "Package received", "bg-[#e6f4ff] text-[#0b5f8a]"],
  ["paid", "Payment sent", "bg-[#dcf7ef] text-[#0b6f65]"],
];
const norm = (s) => (s === "contacted" ? "offer_sent" : s === "completed" ? "paid" : s || "new");
const inp = "h-9 rounded-full border border-[#dcebe8] px-3 text-[13px] outline-none focus:border-[#0e8c7f]";

const PendingReviews = ({ owner }) => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const pending = items.filter((r) => r.status === "pending");
  if (!pending.length) return null;
  const approve = async (r) => { await api.post(`/admin/reviews/${r.id}/approve`); toast.success("Review published on the website"); load(); };
  const reject = async (r) => { await api.delete(`/admin/reviews/${r.id}`); toast.success("Review removed"); load(); };
  return (
    <div className="mb-6 rounded-2xl border border-[#f3e2bb] bg-[#fffaf0] p-5" data-testid="pending-reviews">
      <h3 className="flex items-center gap-2 text-[16px] font-bold text-[#7a4f05]"><Star size={17} fill="currentColor" />New customer reviews ({pending.length})</h3>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {pending.map((r) => (
          <div key={r.id} className="rounded-xl bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#0f2e2a]">{r.name}{r.location && <span className="font-normal text-[#6b7c79]"> · {r.location}</span>}</span>
              <span className="flex text-[#f59e0b]">{[...Array(r.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
            </div>
            <p className="mt-2 text-[14px] text-[#2f4441]">“{r.text}”</p>
            {owner ? (
              <div className="mt-3 flex gap-2">
                <button onClick={() => approve(r)} className="flex items-center gap-1 rounded-full bg-[#0e8c7f] px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-[#0b7569]" data-testid="approve-review-btn"><Check size={14} />Publish</button>
                <button onClick={() => reject(r)} className="flex items-center gap-1 rounded-full border border-[#dcebe8] px-3 py-1.5 text-[13px] font-semibold text-[#4b5d5a] hover:bg-[#f4f8f7]"><X size={14} />Dismiss</button>
              </div>
            ) : <p className="mt-2 text-[12px] text-[#8a9a97]">The main admin can publish this review.</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const QuoteCard = ({ q, reload }) => {
  const [amount, setAmount] = useState(q.amount || "");
  const [method, setMethod] = useState(q.method || "");
  const [tracking, setTracking] = useState(q.tracking || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/quotes/${q.id}`, body); if (msg) toast.success(msg); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = (v) => patch({ status: v }, v === "paid" ? "Marked as paid. Payment email sent to the seller." : q.email ? "Status updated. Seller notified by email." : "Status updated (no seller email on file)");
  const askReview = async () => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/review-request`);
      toast.success(data.sent_via?.length ? `Review request sent by ${data.sent_via.join(" & ")}` : "Review link created", { description: data.link });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const st = STATUSES.find((s) => s[0] === norm(q.status)) || STATUSES[0];
  return (
    <div className="rounded-2xl border border-[#e3f0ed] bg-white p-5" data-testid="quote-item">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-[16.5px] font-bold text-[#0f2e2a]">
            {q.name}
            {q.lang === "es" && <span className="rounded-md bg-[#fff1d6] px-2 py-0.5 text-[11px] font-bold text-[#8a5a00]" title="Seller emails are sent in Spanish" data-testid="quote-lang-es">ES</span>}
            {q.code && <span className="rounded-md bg-[#f2f5f4] px-2 py-0.5 font-mono text-[12px] text-[#4b5d5a]" data-testid="quote-code-badge">{q.code}</span>}
            {q.source === "chat" && <span className="rounded-full bg-[#e0efff] px-2 py-0.5 text-[11px] font-semibold text-[#1d5fb3]">From live chat</span>}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[13.5px] text-[#5b6d6a]">
            {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-1 hover:text-[#0e8c7f]"><Mail size={13} />{q.email}</a>}
            {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-1 hover:text-[#0e8c7f]"><Phone size={13} />{q.phone}</a>}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold ${st[2]}`}>{st[1]}</span>
      </div>
      {q.items && <p className="mt-3 rounded-xl bg-[#f4fbf9] px-3.5 py-2.5 text-[14px] text-[#1f3a36]"><b>Supplies:</b> {q.items}</p>}
      {q.message && <p className="mt-2 text-[14px] text-[#4b5d5a]">{q.message}</p>}
      {q.images?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="quote-images">
          {q.images.map((u) => (
            <a key={u} href={imgUrl(u)} target="_blank" rel="noreferrer" className="block h-16 w-16 overflow-hidden rounded-lg border border-[#e3f0ed] hover:opacity-90">
              <img src={imgUrl(u)} alt="Seller photo" className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input className={`${inp} w-[110px]`} placeholder="Payout $" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={() => amount !== (q.amount || "") && patch({ amount }, "Payout saved")} data-testid="quote-amount-input" />
        <input className={`${inp} w-[120px]`} placeholder="Zelle, PayPal…" value={method} onChange={(e) => setMethod(e.target.value)} onBlur={() => method !== (q.method || "") && patch({ method }, "Payment method saved")} data-testid="quote-method-input" />
        <select className={`${inp} w-[84px]`} value={q.carrier || detectCarrier(tracking) || ""} onChange={(e) => patch({ carrier: e.target.value }, "Carrier saved")} data-testid="quote-carrier-select"><option value="">Carrier</option><option value="usps">USPS</option><option value="ups">UPS</option></select>
        <input className={`${inp} w-[190px] font-mono`} placeholder="Tracking number" value={tracking} onChange={(e) => setTracking(e.target.value)} onBlur={() => tracking !== (q.tracking || "") && patch({ tracking }, "Tracking number saved")} data-testid="quote-tracking-input" />
        {q.tracking && <a href={trackingUrl(q.tracking, q.carrier || detectCarrier(q.tracking) || "usps")} target="_blank" rel="noreferrer" className="text-[12.5px] font-semibold text-[#0e8c7f] hover:underline" data-testid="quote-tracking-link">Track</a>}
        <Select value={norm(q.status)} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[180px] rounded-full" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-[12.5px] text-[#8a9a97]">{new Date(q.created_at).toLocaleString()}</span>
        <div className="flex items-center gap-1.5">
          <button onClick={askReview} className="flex items-center gap-1 rounded-full border border-[#f3e2bb] px-3 py-1.5 text-[12.5px] font-semibold text-[#a0620a] hover:bg-[#fffaf0]" title="Send the seller a review link by email/SMS" data-testid="review-request-btn">
            <Send size={13} />{q.review_requested_at ? "Review asked ✓" : "Ask for review"}
          </button>
          <button onClick={async () => { if (window.confirm("Delete this request?")) { await api.delete(`/quotes/${q.id}`); reload(); } }} className="rounded-lg p-2 text-red-500 hover:bg-red-50" aria-label="Delete"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
};

const QuotesTab = ({ owner }) => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader2 className="animate-spin text-[#0e8c7f]" />;
  return (
    <div data-testid="quotes-tab">
      <PendingReviews owner={owner} />
      <p className="mb-4 text-[14px] text-[#5b6d6a]">Move each quote through the steps. Sellers can follow along at <b>/track</b> with their quote number. The seller is <b>emailed automatically at every step</b> (offer sent, package on the way, package received). <b>Payment sent</b> emails them a payment confirmation and a review link. Enter the payout amount <b>before</b> changing the status so it shows in the email.</p>
      {!items.length ? (
        <div className="rounded-2xl border border-[#e3f0ed] bg-white p-12 text-center text-[#6b7c79]"><Inbox className="mx-auto mb-2 text-[#b6cbc7]" />No quote requests yet.</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{items.map((q) => <QuoteCard key={q.id} q={q} reload={load} />)}</div>
      )}
    </div>
  );
};

export default QuotesTab;
